import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Rocket, History, Info } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api, apiErrorMessage } from "@/lib/api";
import { COMMISSION_MODES, SCOPE_LABELS } from "@/pages/motor/shared";

export const CentroFormulas = () => {
  const qc = useQueryClient();
  const activeQ = useQuery({ queryKey: ["formula-active"], queryFn: async () => (await api.get("/formulas/active")).data });
  const versionsQ = useQuery({ queryKey: ["formula-versions"], queryFn: async () => (await api.get("/formulas/versions")).data });
  const quotesQ = useQuery({ queryKey: ["quotes"], queryFn: async () => (await api.get("/quotes")).data });

  const [partners, setPartners] = useState([]);
  const [globals, setGlobals] = useState({});
  const [pubOpen, setPubOpen] = useState(false);
  const [pub, setPub] = useState({ label: "", notes: "", scope: "future", specific_quote_ids: [] });

  useEffect(() => {
    if (activeQ.data) {
      setPartners((activeQ.data.partners || []).map((p) => ({ ...p })));
      setGlobals({ ...(activeQ.data.globals || {}) });
    }
  }, [activeQ.data]);

  const setP = (i, k, v) => setPartners((s) => s.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const setG = (k, v) => setGlobals((s) => ({ ...s, [k]: v }));

  const publishMut = useMutation({
    mutationFn: () => api.post("/formulas/versions", {
      label: pub.label,
      notes: pub.notes,
      scope: pub.scope,
      specific_quote_ids: pub.scope === "specific" ? pub.specific_quote_ids : [],
      globals: {
        spain_markup_base: globals.spain_markup_base,
        foreign_markup_base: globals.foreign_markup_base,
        paypal_fee_pct: parseFloat(globals.paypal_fee_pct) || 0,
        default_fx_eur_usd: parseFloat(globals.default_fx_eur_usd) || 0,
      },
      partners: partners.map((p) => ({
        key: p.key,
        label: p.label,
        markup_pct: parseFloat(p.markup_pct) || 0,
        extra_uplift_pct: parseFloat(p.extra_uplift_pct) || 0,
        commission_pct: parseFloat(p.commission_pct) || 0,
        commission_mode: p.commission_mode || "none",
      })),
    }),
    onSuccess: (res) => {
      const n = res.data.affected_quotes;
      toast.success(`Versión v${res.data.version} publicada · ${n} cotización(es) recalculada(s)`);
      qc.invalidateQueries({ queryKey: ["formula-active"] });
      qc.invalidateQueries({ queryKey: ["formula-versions"] });
      qc.invalidateQueries({ queryKey: ["formula-partners"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      setPubOpen(false);
      setPub({ label: "", notes: "", scope: "future", specific_quote_ids: [] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const quotes = quotesQ.data?.quotes || [];
  const versions = versionsQ.data?.versions || [];

  if (activeQ.isLoading) {
    return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  return (
    <div className="space-y-4" data-testid="centro-formulas">
      <Card className="border-info/30 bg-info/5">
        <CardContent className="flex items-start gap-3 p-4 text-sm">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" />
          <p className="text-muted-foreground">Edita las reglas y pulsa <strong>Publicar nueva versión</strong>. Podrás elegir si el cambio afecta a <strong>viajes futuros</strong>, a <strong>todos</strong> o a <strong>específicos</strong>. Cada cotización guarda la versión usada.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Reglas globales</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Base markup España</Label>
            <Select value={globals.spain_markup_base} onValueChange={(v) => setG("spain_markup_base", v)}>
              <SelectTrigger data-testid="g-spain-base"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="tax_excl">Sin IVA</SelectItem><SelectItem value="tax_incl">Con IVA</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Base markup extranjero</Label>
            <Select value={globals.foreign_markup_base} onValueChange={(v) => setG("foreign_markup_base", v)}>
              <SelectTrigger data-testid="g-foreign-base"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="tax_excl">Sin IVA</SelectItem><SelectItem value="tax_incl">Con IVA</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Recargo PayPal (%)</Label><Input type="number" step="0.1" value={globals.paypal_fee_pct ?? ""} onChange={(e) => setG("paypal_fee_pct", e.target.value)} data-testid="g-paypal" /></div>
          <div className="space-y-1.5"><Label className="text-xs">FX €→$ por defecto</Label><Input type="number" step="0.0001" value={globals.default_fx_eur_usd ?? ""} onChange={(e) => setG("default_fx_eur_usd", e.target.value)} data-testid="g-fx" /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Reglas por partner</CardTitle>
          <Button onClick={() => setPubOpen(true)} data-testid="open-publish"><Rocket className="mr-1.5 h-4 w-4" />Publicar nueva versión</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="hidden grid-cols-12 gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
            <span className="col-span-3">Partner</span><span className="col-span-2">Markup %</span><span className="col-span-2">Extra % (uplift)</span><span className="col-span-2">Comisión %</span><span className="col-span-3">Modo comisión</span>
          </div>
          {partners.map((p, i) => (
            <div key={p.key} className="grid grid-cols-1 items-center gap-2 rounded-lg border p-2 sm:grid-cols-12" data-testid={`partner-rule-${p.key}`}>
              <div className="text-sm font-medium sm:col-span-3">{p.label}</div>
              <div className="sm:col-span-2"><Input type="number" step="0.1" value={p.markup_pct} onChange={(e) => setP(i, "markup_pct", e.target.value)} data-testid={`markup-${p.key}`} /></div>
              <div className="sm:col-span-2"><Input type="number" step="0.1" value={p.extra_uplift_pct} onChange={(e) => setP(i, "extra_uplift_pct", e.target.value)} data-testid={`uplift-${p.key}`} /></div>
              <div className="sm:col-span-2"><Input type="number" step="0.1" value={p.commission_pct} onChange={(e) => setP(i, "commission_pct", e.target.value)} data-testid={`commission-${p.key}`} /></div>
              <div className="sm:col-span-3">
                <Select value={p.commission_mode} onValueChange={(v) => setP(i, "commission_mode", v)}>
                  <SelectTrigger data-testid={`mode-${p.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{COMMISSION_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" />Historial de versiones</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/60" data-testid={`version-row-${v.version}`}>
              <Badge variant={v.is_active ? "default" : "outline"} className="text-[10px]">v{v.version}{v.is_active ? " · activa" : ""}</Badge>
              <span className="min-w-0 flex-1 truncate text-sm">{v.label}</span>
              <Badge variant="secondary" className="text-[10px]">{SCOPE_LABELS[v.scope] || v.scope}</Badge>
              <span className="shrink-0 text-xs text-muted-foreground">{v.created_by}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={pubOpen} onOpenChange={setPubOpen}>
        <DialogContent data-testid="publish-dialog">
          <DialogHeader>
            <DialogTitle>Publicar nueva versión de fórmula</DialogTitle>
            <DialogDescription>Elige a qué cotizaciones afecta este cambio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nombre / etiqueta</Label><Input value={pub.label} onChange={(e) => setPub({ ...pub, label: e.target.value })} placeholder="Ej: Subida markup KimKim feb-2026" data-testid="publish-label" /></div>
            <div className="space-y-2">
              <Label>Alcance del cambio</Label>
              <RadioGroup value={pub.scope} onValueChange={(v) => setPub({ ...pub, scope: v })}>
                <label className="flex items-start gap-2 rounded-md border p-2.5 text-sm"><RadioGroupItem value="future" id="sc-future" data-testid="scope-future" /><div><p className="font-medium">Solo viajes futuros</p><p className="text-xs text-muted-foreground">Las cotizaciones existentes no cambian.</p></div></label>
                <label className="flex items-start gap-2 rounded-md border p-2.5 text-sm"><RadioGroupItem value="all" id="sc-all" data-testid="scope-all" /><div><p className="font-medium">Todos los viajes</p><p className="text-xs text-muted-foreground">Recalcula todas las cotizaciones existentes.</p></div></label>
                <label className="flex items-start gap-2 rounded-md border p-2.5 text-sm"><RadioGroupItem value="specific" id="sc-spec" data-testid="scope-specific" /><div><p className="font-medium">Viajes específicos</p><p className="text-xs text-muted-foreground">Elige cuáles recalcular.</p></div></label>
              </RadioGroup>
            </div>
            {pub.scope === "specific" && (
              <div className="space-y-1.5">
                <Label>Cotizaciones a recalcular</Label>
                <ScrollArea className="h-40 rounded-md border p-2">
                  {quotes.length === 0 ? <p className="p-2 text-xs text-muted-foreground">No hay cotizaciones.</p> : quotes.map((q) => (
                    <label key={q.id} className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/50">
                      <Checkbox checked={pub.specific_quote_ids.includes(q.id)} onCheckedChange={() => setPub((s) => ({ ...s, specific_quote_ids: s.specific_quote_ids.includes(q.id) ? s.specific_quote_ids.filter((x) => x !== q.id) : [...s.specific_quote_ids, q.id] }))} data-testid={`pick-quote-${q.id}`} />
                      <span className="truncate">{q.name}</span>
                    </label>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPubOpen(false)}>Cancelar</Button>
            <Button onClick={() => publishMut.mutate()} disabled={!pub.label || publishMut.isPending} data-testid="publish-button">{publishMut.isPending ? "Publicando…" : "Publicar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
